import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { FEATURE_FLAGS_COLLECTION } from '../board/boardGuideRailFlag';
import { useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { GIFT_GUIDE_HUB_MEMORY_KEY, resolvePreviewOverride } from '../lib/previewMemory';

/**
 * The ONE resolver for the Gift Guide Hub prototype (THE-581; THE-577 plan
 * §0 "Access: how the prototype is gated"). Every surface the hub owns gates
 * on this single resolved value.
 *
 *   giftGuideHubGate = ?giftGuideHub=1          -> 'enabled'  (preview cohort;
 *                        or remembered from one in this browser, unless the
 *                        URL says ?giftGuideHub=0 — lib/previewMemory.ts)
 *                      : featureFlags/giftGuideHub.enabled === true
 *                          ? 'enabled' : 'disabled'
 *                      : ...and 'pending' until that read answers.
 *
 * THREE STATES, NOT TWO (THE-664). This resolver used to return a `boolean`,
 * which forced the unanswered-yet state to share a value with "off" — and the
 * release door reaches its answer through `ensureAuth()` + `getDoc()`, so at
 * GENERAL RELEASE every visitor's FIRST render read `false`. That is not a
 * race, it is deterministic: `TopicHubPage` turned that one render into
 * `<Navigate replace>` and every shared `/gift-guides/:topic` link — bookmark,
 * SEO result, occasion deep-link — was bounced to the index before the flag
 * resolved, with `replace` denying the visitor even the back button. The bug
 * was invisible because BOTH doors we ever tested through resolve synchronously:
 * the `?giftGuideHub=1` param short-circuits the read, and every hub test
 * hard-mocks the gate to on.
 *
 * So `pending` is a first-class value and consumers must handle all three. A
 * consumer that only distinguishes "on" from "not on" reintroduces the defect,
 * which is why there is no boolean wrapper here to reach for: `'disabled'` is a
 * truthy string, and `if (!gate)` would be quietly always-false.
 *
 * SECOND INSTANCE OF A SHIPPED MECHANISM, DELIBERATELY NOT A SECOND
 * MECHANISM. `src/theaWeb/board/boardGuideRailFlag.ts` is the first (THE-436,
 * thea-web #495) and this file is its twin: same two doors, same precedence,
 * same fail-closed read, same param-carrying helper. Where the two differ it
 * is a bug in one of them, not a design choice — read them side by side.
 * `FEATURE_FLAGS_COLLECTION` is imported from the rail rather than re-declared
 * so the two flags can never drift onto different collections.
 *
 * TWO DOORS, DIFFERENT JOBS:
 *
 *  - `?giftGuideHub=1` is the PREVIEW-COHORT door. Hand a reviewer a
 *    givethea.com link carrying the param and they get the hub while the
 *    general-release switch stays off. It takes precedence, and
 *    `enabled: false` does NOT revoke it — this is a release switch, not a
 *    universal kill switch. Because the param is the cohort's only carrier it
 *    has to survive navigation: `withGiftGuideHubParam` below is the shared
 *    helper every hop uses. The hub has MORE hops than the rail did, so that
 *    helper is the bulk of this feature, not a footnote — see
 *    `useGuidesIndexHref` / `useGiftGuideHubLink` below and the hop inventory
 *    in `__tests__/giftGuideHubHops.test.tsx`, which fails if a NEW bare
 *    `/gift-guides` link appears anywhere in `src/`.
 *
 *  - `featureFlags/giftGuideHub.enabled` is the GENERAL-RELEASE switch.
 *    Default off; flipping it true launches the hub to everyone with NO
 *    frontend deploy. That is the whole point of building it now: thea-web has
 *    no continuous deploy, so a launch that needed a code change would mean
 *    scheduling a GitHub Actions build and waiting on it.
 *
 * FAIL-CLOSED, AND IT FAILS CLOSED UNTIL THE RULES BLOCK IS DEPLOYED. Any
 * absence/error resolves to `false`. thea-serverless's `firestore.rules` grants
 * client reads on `featureFlags` ONE DOCUMENT AT A TIME —
 * `match /featureFlags/boardGuideRail` — because the collection also holds
 * internal email/agent-spend switches and must stay out of the catch-all
 * allowlist. So `featureFlags/giftGuideHub` needs its OWN block (thea-serverless
 * PR, THE-581); until that block is DEPLOYED this read is permission-denied for
 * every client and the switch resolves false for everyone. Verified against the
 * live deployed ruleset 2026-09-25, not assumed. This is written as a real read
 * rather than a hardcoded `false` precisely so that going live is a rules +
 * flag-doc change with no frontend deploy.
 */

/** Query param carrying the preview-cohort override. */
export const GIFT_GUIDE_HUB_PARAM = 'giftGuideHub';

/** The value that activates the override (and, from a landing URL, remembers
 *  it in this browser). `=0` turns it off and forgets it; any other value
 *  defers to that memory. */
export const GIFT_GUIDE_HUB_PARAM_ON = '1';

/** Firestore location of the general-release switch (server-written). */
export const GIFT_GUIDE_HUB_FLAG_DOC = 'giftGuideHub';

/** Shape of `featureFlags/giftGuideHub`. */
export interface GiftGuideHubFlagDoc {
  enabled?: boolean;
}

/**
 * The resolved state of the hub gate. `pending` means the general-release read
 * has not answered yet and NOTHING has been decided — it is not a synonym for
 * `disabled`, and conflating the two is the THE-664 defect. Every consumer
 * branches on all three.
 */
export type GiftGuideHubGate = 'pending' | 'enabled' | 'disabled';

/**
 * Is the cohort override on for this location? `?giftGuideHub=1` -> true,
 * `?giftGuideHub=0` -> false, otherwise whatever this browser remembers from an
 * earlier tester link (`lib/previewMemory.ts`; `?freshFindFridays=1` remembers
 * it too) — so a bare `search` can be true, and a hop that forgot to carry the
 * param no longer drops the tester. Synchronous and Router-free, so the URL
 * helper and the hooks share one definition of "the override is on" and a test
 * can drive it without a Router.
 */
export function hasGiftGuideHubOverride(search: string): boolean {
  return resolvePreviewOverride(search, GIFT_GUIDE_HUB_PARAM, GIFT_GUIDE_HUB_MEMORY_KEY);
}

/**
 * Append `?giftGuideHub=1` to an in-app path when the override is active — the
 * shared helper for EVERY hop that has to carry the cohort forward.
 *
 * Deliberately string surgery on a relative path rather than `new URL()`:
 * these are router paths ('/gift-guides', '/guide/little-artist'), not absolute
 * URLs, and `new URL()` needs a base. Preserves any existing query and hash,
 * and is idempotent — carrying the param through a hub -> guide -> back round
 * trip must not produce `?x=1&x=1`.
 */
export function withGiftGuideHubParam(path: string, active: boolean): string {
  if (!active || !path) return path;
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const search = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
  const params = new URLSearchParams(search);
  // set(), not append() — idempotence.
  params.set(GIFT_GUIDE_HUB_PARAM, GIFT_GUIDE_HUB_PARAM_ON);
  return `${pathname}?${params.toString()}${hash}`;
}

/**
 * Is the cohort override on for the CURRENT location? Reactive on the URL, so
 * a client-side nav that drops or adds the param flips it without a remount.
 *
 * This is the input to every param-carrying hop, and it is deliberately the
 * OVERRIDE ALONE rather than the resolved gate: once the release switch is on
 * the hub renders for everyone with no param, so stamping one onto every link
 * would litter the whole site's URLs to no effect.
 */
export function useGiftGuideHubOverride(): boolean {
  const { search } = useLocation();
  return hasGiftGuideHubOverride(search);
}

/**
 * The guides-index route. Exported so every hop reaches the index through one
 * constant instead of retyping the string — the hop inventory test greps for
 * bare occurrences of it and this is the single sanctioned one.
 */
export const GUIDES_INDEX_PATH = '/gift-guides';

/**
 * The cohort carrier, as a function of the CURRENT location: give it any in-app
 * path and get that path with `?giftGuideHub=1` appended if — and only if — the
 * cohort param is on the URL right now.
 *
 * This is the hook every hop uses. There are more hops here than the rail had,
 * and they are not all on the hub page: the cohort lives in the URL, so ANY
 * link a tester can click while carrying the param is a place the param can be
 * dropped. The full inventory (asserted in `__tests__/giftGuideHubHops.test.tsx`):
 *
 *   H1  hub -> guide card                     GiftGuidesPage -> GuideBrowse
 *   H2  guide -> back to hub                  useGuideBackLink (`fromHub` state)
 *   H3  guide -> "All Gift Guides" (mobile)   GuideBoardView header slot
 *   H4  guide -> "All Gift Guides" (desktop)  PublicBoardBackBreadcrumb
 *   H5  guide error/empty -> index            GuidePage's three <Link>s
 *   H6  bottom tab bar -> index               MobileTabBar (and V4MobileTabBar,
 *                                             which renders the same bar)
 *   H7  desktop nav -> index                  useDesktopNav
 *   H8  homepage rail heading -> index        HomeGuideRail
 *   H9  homepage rail card -> guide           HomeGuideRail
 *   H10 board rail heading -> index           BoardGuideRail
 *   H11 board rail card -> guide              BoardGuideRail (composes with the
 *                                             rail's OWN cohort param)
 *   H15 guide -> back to the board            useGuideBackLink's board branch —
 *                                             the RETURN leg of H11 (THE-619)
 *   H12 hub -> quiz                           GiftGuideHub's two CTAs (THE-583)
 *   H13 guide -> quiz                         all 29 guide containers, through
 *                                             useGuideQuizNavigate (THE-606)
 *   H14 calendar empty state -> quiz          CalendarPage (THE-606)
 *   H16 topic hub -> quiz                     TopicHubPage's save-flow CTA, on
 *                                             /gift-guides/:topic (THE-606)
 *
 * H6/H7 are the ones a rail-shaped reading of this feature misses and the ones
 * a tester hits first: the hub is a TAB, so the natural second move is Calendar
 * and back, and a bare tab link silently returns today's page.
 *
 * H15 is H11 read backwards, and it is why an inventory of OUTBOUND hops is not
 * enough: H11 carries the cohort onto the guide correctly and the back link then
 * threw it away, so a tester who used the rail round trip the way it was meant
 * to be used ended up bare. It is pinned behaviourally in
 * `guide/__tests__/guideBoardBackLinkHop.test.tsx` rather than by a third
 * source-grep census; that file heads with the reasoning, including the two
 * `/quiz/results` navigations (regenerate and post-quiz-submit) that still drop
 * the cohort and own the census when they are fixed.
 *
 * H12–H14 and H16 are the quiz CTAs (THE-606), which landed second and kept
 * their own numbers per the note this paragraph used to carry — hence H16 rather
 * than a tidy H15, which THE-619 had already taken. H13 is the one that mattered
 * most: hub -> guide card -> quiz is the prototype's PRIMARY path, and 29 guide
 * containers each held a byte-identical copy of the drop. They now share ONE
 * composition (`guide/useGuideQuizNavigate.ts`), and the `/quiz` half of the hop
 * census asserts `guideQuizPath` has exactly one importer — so a new guide page
 * cannot reach the un-carrying builder at all. That structural half is the point:
 * the FIRST census keyed only on `'/gift-guides'` literals, which structurally
 * could not see a `/quiz` literal, which is why H13 shipped broken.
 *
 * Returns a stable callback so callers can put it in a `useMemo` dep list.
 */
export function useGiftGuideHubLink(): (path: string) => string {
  const active = useGiftGuideHubOverride();
  return useCallback((path: string) => withGiftGuideHubParam(path, active), [active]);
}

/**
 * The guides-index href for the current location — `/gift-guides`, carrying the
 * cohort param when the tester has it. The shorthand for the many hops whose
 * destination is just the index (H3–H8, H10).
 */
export function useGuidesIndexHref(): string {
  return useGiftGuideHubLink()(GUIDES_INDEX_PATH);
}

export interface UseGiftGuideHubGateOptions {
  /**
   * Skip the release-switch read entirely. A parameter rather than an `if`
   * because hooks can't be conditional (the same opt-out shape
   * `useBoardGuideRailEnabled(skip)` uses).
   */
  skip?: boolean;
}

/**
 * The general-release switch alone. Split out from the resolver below so tests
 * can exercise the Firestore half without a query string, and so the resolver
 * reads as the plan's one-line expression.
 *
 * EVERY EXIT SETTLES (THE-664). The old version only ever called its setter on
 * the one happy path — `enabled === true` — so "missing doc", "denied" and
 * "still in flight" were all the initial `false`, and a caller could not tell a
 * fail-closed answer from no answer at all. Now the absent/denied/offline cases
 * settle explicitly on `'disabled'`: still fail-closed, but as a decision the
 * consumer can act on rather than a state it has to guess about.
 */
function useGiftGuideHubRelease(skip: boolean): GiftGuideHubGate {
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [resolved, setResolved] = useState<'enabled' | 'disabled' | null>(null);

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await ensureAuth();
        const snap = await getDoc(
          doc(db, FEATURE_FLAGS_COLLECTION, GIFT_GUIDE_HUB_FLAG_DOC),
        );
        if (cancelled) return;
        const data = snap.exists()
          ? (snap.data() as GiftGuideHubFlagDoc)
          : undefined;
        // `=== true` not truthiness: a stray string in the flag doc must not
        // launch a prototype to everyone.
        setResolved(data?.enabled === true ? 'enabled' : 'disabled');
      } catch {
        // Denied (today's rules, until the thea-serverless block deploys),
        // offline, or absent — all mean "not released". Deliberately silent on
        // the console: this read is EXPECTED to fail until the rules block
        // lands, and a warning on every guides-tab render would be noise that
        // trains people to ignore the console. It is NOT silent to the caller —
        // it settles, because a gate that stayed `pending` on a permission
        // denial would hold the topic route on a loading shell forever.
        if (!cancelled) setResolved('disabled');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, ensureAuth, skip]);

  // A skipped read never answers, and a caller that skips has already decided
  // the switch cannot change this render's outcome — so report the settled
  // fail-closed value rather than a `pending` nobody is waiting on. Derived
  // rather than seeded into `useState`, because `skip` flips at runtime when a
  // client-side nav adds or drops the cohort param.
  if (skip) return 'disabled';
  return resolved ?? 'pending';
}

/**
 * The resolved gate: cohort override OR general release, as one of three
 * states. Reactive on the URL for the same reason the rail's resolver is.
 *
 * The override wins and resolves SYNCHRONOUSLY — a preview link never sees
 * `pending`, which is exactly why the preview door hid this bug for the whole
 * prototype (see the header).
 */
export function useGiftGuideHubGate(
  options: UseGiftGuideHubGateOptions = {},
): GiftGuideHubGate {
  const override = useGiftGuideHubOverride();
  // When the override already decided `enabled`, the release read can only
  // agree — skip it rather than spend a (currently denied) Firestore round trip.
  const released = useGiftGuideHubRelease(options.skip === true || override);
  return override ? 'enabled' : released;
}
